import React, { useState } from 'react';
import { Box, Button, FormControlLabel, IconButton, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchFittingsByClass } from '../../api/fittings';
import { clearSightings, fetchSightings, reportSighting, SightingCategory, SiteSighting } from '../../api/sites';
import ConsolePanel from '../../components/ConsolePanel';
import SuggestedFittings from '../../components/SuggestedFittings';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../services/AuthContext';
import { C, FONT, frameSx, glow, hexToRgba, prefersReducedMotion } from '../../theme/tokens';
import { useSystemBase, useSystemIntel } from './SystemPage';
import { timeAgo } from './KillRow';
import { msUntil } from '../../services/time';
import { parseSitePaste } from './sitePaste';

/** What can spawn here, grouped by what you'd actually bring a ship for.
 * Each category explains itself once — a newbro shouldn't have to already
 * know that "relic" means hacking — and every site name links through to
 * its own NPC composition / loot / trigger detail. A member can also mark
 * a site as currently up (`site_sightings`, self-expiring 4-8h) so anyone
 * else reading this page sees a live "seen recently" signal, not just the
 * static catalog of what CAN spawn here. */
const CATEGORIES: { key: string; label: string; accent: string; blurb: string; sightingCategory: SightingCategory }[] = [
  { key: 'combat', label: 'Combat', accent: C.red, blurb: 'Sleeper anomalies — trigger ship spawns the next wave.', sightingCategory: 'combat' },
  { key: 'relicData', label: 'Relic & Data', accent: C.gold, blurb: 'Hacking sites — needs an Analyzer; most have Sleeper guards.', sightingCategory: 'relic' },
  { key: 'gas', label: 'Gas', accent: C.cyan, blurb: 'Gas Cloud Harvester required — guard rats arrive ~20 min after activation.', sightingCategory: 'gas' },
  { key: 'ore', label: 'Ore', accent: C.cyan, blurb: 'Mining sites — guard rats arrive on a timer once active.', sightingCategory: 'ore' },
];

const hoursLeft = (expiresAt: string): string => {
  // expires_at is SQLite's zoneless UTC — parsed raw it was wrong by the
  // reader's offset and Invalid Date on Safari. See services/time.ts.
  const ms = msUntil(expiresAt);
  if (ms === null) return 'unknown';
  if (ms <= 0) return 'expired';
  const h = Math.floor(ms / 3_600_000);
  const m = Math.round((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
};

/**
 * The "is this still worth flying to" badge — one report reads as a plain
 * countdown, more than one flips to gold and states the count, because that
 * second report is the one piece of information this app can't resolve on
 * its own (same site confirmed twice, or a genuine second one).
 *
 * The pop+glow plays once per count change, keyed on `sightings.length` so
 * React treats a fresh confirmation as a new element instead of a silent
 * prop update — the one authored moment this badge gets, not a standing
 * loop, so it reads as "something just happened" rather than as decoration.
 */
const SightingBadge: React.FC<{ sightings: SiteSighting[]; reducedMotion: boolean }> = ({ sightings, reducedMotion }) => {
  const newest = sightings[0];
  const multi = sightings.length > 1;
  const accent = multi ? C.gold : C.green;

  return (
    <Tooltip
      title={
        multi ? (
          <Stack spacing={0.4} sx={{ py: 0.25 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'inherit' }}>
              {sightings.length} independent reports, still active
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: 'inherit', opacity: 0.85 }}>
              Could be the same site confirmed twice, or genuinely two up.
            </Typography>
            <Stack spacing={0.15} sx={{ mt: 0.25 }}>
              {sightings.map((s) => (
                <Typography key={s.id} sx={{ fontFamily: FONT.mono, fontSize: '0.66rem', color: 'inherit' }}>
                  {s.reported_by} &middot; {timeAgo(s.created_at)}
                </Typography>
              ))}
            </Stack>
          </Stack>
        ) : (
          `${newest.reported_by} confirmed ${timeAgo(newest.created_at)} — ${hoursLeft(newest.expires_at)}`
        )
      }
    >
      <Box
        key={sightings.length}
        sx={{
          cursor: 'default',
          display: 'inline-flex',
          animation: reducedMotion ? 'none' : 'sightingPop 0.5s cubic-bezier(0.16,1,0.3,1) both',
          '@keyframes sightingPop': {
            '0%': { opacity: 0, transform: 'scale(0.55)', filter: 'drop-shadow(0 0 0px transparent)' },
            '55%': { opacity: 1, transform: 'scale(1.16)', filter: `drop-shadow(${glow(accent, 7, 0.85)})` },
            '100%': { opacity: 1, transform: 'scale(1)', filter: 'drop-shadow(0 0 0px transparent)' },
          },
        }}
      >
        <StatusBadge
          label={multi ? `×${sightings.length} confirmed · ${hoursLeft(newest.expires_at)}` : `Confirmed · ${hoursLeft(newest.expires_at)}`}
          accent={accent}
          icon={<VisibilityIcon sx={{ fontSize: 13 }} />}
        />
      </Box>
    </Tooltip>
  );
};

const SystemSites: React.FC = () => {
  const intel = useSystemIntel();
  const navigate = useNavigate();
  const base = useSystemBase();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const { system, siteCatalog, gasSites, oreSites } = intel;
  const whClass = system.system_class;
  const reducedMotion = prefersReducedMotion();
  const [paste, setPaste] = useState('');
  const [onlyPasted, setOnlyPasted] = useState(false);

  const classFits = useQuery({
    queryKey: ['fittings-by-class', whClass],
    queryFn: () => fetchFittingsByClass(whClass!),
    enabled: !!whClass,
  });

  const sightings = useQuery({
    queryKey: ['sightings', system.solar_system_id],
    queryFn: () => fetchSightings(system.solar_system_id),
  });
  // Every active (non-expired) report for a site name, newest first — not
  // collapsed to one. WH sites carry no in-game identity beyond their name,
  // so this app can never prove a second report is a genuinely separate
  // instance rather than the same one confirmed twice; showing the raw
  // count is the honest answer instead of silently picking one report and
  // hiding that anyone else saw anything.
  const sightingsByName = new Map<string, SiteSighting[]>();
  for (const s of sightings.data ?? []) {
    const list = sightingsByName.get(s.site_name);
    if (list) list.push(s);
    else sightingsByName.set(s.site_name, [s]);
  }

  const reportMutation = useMutation({
    mutationFn: ({ category, siteName }: { category: SightingCategory; siteName: string }) =>
      reportSighting(system.solar_system_id, category, siteName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sightings', system.solar_system_id] });
      queryClient.invalidateQueries({ queryKey: ['system-activity', system.solar_system_id] });
    },
  });

  const clearMutation = useMutation({
    mutationFn: ({ category, siteName }: { category: SightingCategory; siteName: string }) =>
      clearSightings(system.solar_system_id, category, siteName),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sightings', system.solar_system_id] }),
  });

  if (!siteCatalog) {
    return (
      <ConsolePanel title="No Site Catalog" accent={C.neutral} corners={false} sx={frameSx(C.neutral)}>
        <Typography sx={{ color: C.textDim }}>
          {system.name} isn&rsquo;t wormhole space, so it has no Sleeper site catalog.
        </Typography>
      </ConsolePanel>
    );
  }

  const namesFor = (key: string) =>
    key === 'combat'
      ? siteCatalog.combatSites
      : key === 'relicData'
        ? siteCatalog.relicDataSites
        : key === 'gas'
          ? gasSites
          : oreSites;
  const bucketFor = (key: string) => (key === 'gas' ? 'gas' : key === 'ore' ? 'ore' : (whClass ?? 'unknown'));
  // Recomputed on every keystroke: the parse is a few hundred comparisons, so
  // nothing here needs memoising.
  const allNames = CATEGORIES.flatMap((cat) => namesFor(cat.key));
  const pasted = paste.trim() ? parseSitePaste(paste, allNames) : null;
  const isPasted = (name: string) => !!pasted?.matched.has(name);
  const pastedHere = allNames.filter(isPasted).length;

  const openSite = (bucket: string, name: string) =>
    navigate(
      `${base}/sites/${encodeURIComponent(bucket)}/${encodeURIComponent(name)}?system=${system.solar_system_id}`,
    );

  return (
    <Stack spacing={2}>
      <ConsolePanel title="Paste a list" accent={C.cyan} corners={false} sx={frameSx(C.cyan)}>
        <Typography sx={{ color: C.textDim, fontSize: '0.82rem', mb: 1.25 }}>
          Paste site names in English or German, one per line or straight from a scanner table. Matches move to the top
          and glow; the rest grey out.
        </Typography>
        <TextField
          multiline
          minRows={2}
          fullWidth
          size="small"
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          placeholder="One site per line"
          inputProps={{ 'aria-label': 'Site names to highlight' }}
        />
        {pasted && (
          <Stack direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
            <StatusBadge label={`${pastedHere} on this page`} accent={pastedHere > 0 ? C.green : C.neutral} />
            {pasted.unmatched.length > 0 && (
              <Tooltip title={pasted.unmatched.slice(0, 8).join(' · ')}>
                <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.72rem', cursor: 'default' }}>
                  {pasted.unmatched.length} {pasted.unmatched.length === 1 ? 'line' : 'lines'} not found
                </Typography>
              </Tooltip>
            )}
            <Box sx={{ flex: 1 }} />
            <FormControlLabel
              control={<Switch size="small" checked={onlyPasted} onChange={(e) => setOnlyPasted(e.target.checked)} />}
              label={<Typography sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: C.textDim }}>Only show these</Typography>}
            />
            <Button
              size="small"
              onClick={() => {
                setPaste('');
                setOnlyPasted(false);
              }}
              sx={{ color: C.textDim }}
            >
              Clear
            </Button>
          </Stack>
        )}
      </ConsolePanel>
      {CATEGORIES.map((cat) => {
        const names = namesFor(cat.key);
        // Matches first, catalogue order kept within each group. "Only show these" drops the rest.
        const shown = names
          .filter((name) => !onlyPasted || isPasted(name))
          .sort((a, b) => Number(isPasted(b)) - Number(isPasted(a)));
        return (
          <ConsolePanel key={cat.key} title={cat.label} accent={cat.accent} corners={false} sx={frameSx(cat.accent)}>
            <Typography sx={{ color: C.textDim, fontSize: '0.82rem', mb: 1.25 }}>{cat.blurb}</Typography>
            {names.length === 0 ? (
              <Typography sx={{ color: C.textDim, fontSize: '0.82rem' }}>
                No {cat.label.toLowerCase()} sites catalogued for this class.
              </Typography>
            ) : shown.length === 0 ? (
              <Typography sx={{ color: C.textDim, fontSize: '0.82rem' }}>Nothing pasted in this category.</Typography>
            ) : (
              <Stack spacing={0.5}>
                {shown.map((name) => {
                  const active = sightingsByName.get(name);
                  const matched = isPasted(name);
                  const dimmed = !!pasted && !matched;
                  return (
                    <Stack
                      key={name}
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{
                        // Matches light up in their category colour; the rest grey out.
                        // Reduced motion gets the end state at once.
                        transition: reducedMotion
                          ? 'none'
                          : 'opacity 0.35s cubic-bezier(0.16,1,0.3,1), filter 0.35s cubic-bezier(0.16,1,0.3,1)',
                        opacity: dimmed ? 0.5 : 1,
                        filter: dimmed ? 'grayscale(1)' : matched ? `drop-shadow(${glow(cat.accent, 8, 0.7)})` : 'none',
                      }}
                    >
                      <Box
                        onClick={() => openSite(bucketFor(cat.key), name)}
                        sx={{ cursor: 'pointer', transition: 'transform 0.15s ease', '&:hover': { transform: 'translateY(-1px)' } }}
                      >
                        <StatusBadge label={name} accent={cat.accent} />
                      </Box>
                      {active && (
                        <SightingBadge
                          sightings={active}
                          reducedMotion={reducedMotion}
                        />
                      )}
                      {/* "It's done" — clears every active report for this
                          site ahead of its natural expiry. No alert fires:
                          removing a badge isn't new information, it's the
                          absence of it. */}
                      {active && me.canEdit && (
                        <Tooltip title="Mark done — clears the badge now instead of waiting out the expiry. No alert is sent.">
                          <IconButton
                            size="small"
                            disabled={clearMutation.isPending}
                            onClick={() => clearMutation.mutate({ category: cat.sightingCategory, siteName: name })}
                            sx={{ p: 0.25, color: C.textDim, '&:hover': { color: C.red } }}
                          >
                            <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Not an else: the badge above only ever shows the
                          count that already exists — this is how a second
                          (or third) independent report actually gets added,
                          so it has to stay clickable once something is
                          already tracked, not disappear the moment it is. */}
                      {me.canEdit && (
                        <Tooltip
                          title={
                            active
                              ? 'Also seeing this up? Report it again to add another confirmation.'
                              : 'Mark this site as currently up (expires in a few hours)'
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => reportMutation.mutate({ category: cat.sightingCategory, siteName: name })}
                            sx={{ p: 0.25, color: C.textDim, '&:hover': { color: cat.accent } }}
                          >
                            <AddCircleOutlineIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </ConsolePanel>
        );
      })}

      {classFits.data && classFits.data.length > 0 && (
        <ConsolePanel title={`Fits tagged for ${whClass}`} accent={C.blue} corners={false} collapsible sx={frameSx(C.blue)}>
          <SuggestedFittings key={system.solar_system_id} fittings={classFits.data} />
        </ConsolePanel>
      )}

      <Typography sx={{ color: hexToRgba(C.neutral, 0.85), fontFamily: FONT.mono, fontSize: '0.68rem' }}>
        Click a site name for its NPC waves, triggers, loot and hacking/gas specifics. &ldquo;Confirmed&rdquo; counts
        down to when the report expires. It turns gold and adds a &ldquo;&times;N&rdquo; when more than one
        independent report is active at once — the app can&rsquo;t tell a second sighting of the same site from a
        genuinely separate one, so it says exactly that instead of guessing. The check mark clears it early once the
        site is actually done, with no alert sent. Sightings are only ever visible to your own corp and alliance.
      </Typography>
    </Stack>
  );
};

export default SystemSites;
